import { jsx as _jsx } from "react/jsx-runtime";
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOverlayContext } from 'seagull';
import { routes } from '~/constants';
import { useEventReporter } from '~/event-reporter';
import { useIntegration } from '~/hooks/useIntegration';
import { handlePromise } from '~/infra/utils';
import { getAccountingDetails } from '~/integrations/Accounting/accounting.utils';
import { selectors } from '~/store/state-selectors';
import { useStore, useStoreActions } from '~/store/store-context';
import { MapAccountsModal } from '../../MapAccountsModal/MapAccountsModal';
import { MapContactsModal } from '../../MapContactsModal/MapContactsModal';
import { MapServicesModal } from '../../MapServicesModal/MapServicesModal';
import { reportQboChooseAccounts, reportQboDisconnect, reportQboMapContacts, reportQboMapServices } from '../AccountingSetupPage.utils';
import { DisconnectAccountingModal } from '../DisconnectAccountingModal';
export const useAccountingSetupActions = (param)=>{
    let { accounting, disconnectModalTestId } = param;
    const { showModal } = useOverlayContext();
    const navigate = useNavigate();
    const { logBI, fetchIntegrations, fetchIntegrationsSummary } = useStoreActions();
    const vendorId = useStore(selectors.user.businessId);
    const userId = useStore(selectors.user.id);
    const reporters = useEventReporter();
    const { disconnect: disconnectFromAccounting } = useIntegration(accounting);
    const [isDisconnecting, setIsDisconnecting] = useState(false);
    const { biCodes, fullName: accountingFullName } = getAccountingDetails(accounting);
    const actorIds = {
        vendorId,
        userId
    };
    const renderDisconnectModal = (param)=>{
        let { dismissModal } = param;
        return /*#__PURE__*/ _jsx(DisconnectAccountingModal, {
            accountingFullName: accountingFullName,
            testId: disconnectModalTestId,
            onDismiss: dismissModal
        });
    };
    const confirmDisconnect = ()=>showModal({
            biName: 'accounting_setup_page_confirm',
            closeOnClickOutside: false,
            closeOnEscapeKey: false,
            content: renderDisconnectModal
        });
    const disconnect = async ()=>{
        if (!await confirmDisconnect()) return;
        setIsDisconnecting(true);
        logBI(biCodes.disconnect, actorIds);
        reportQboDisconnect(accounting, reporters, actorIds);
        await disconnectFromAccounting();
        setIsDisconnecting(false);
        await handlePromise(Promise.all([
            fetchIntegrations(),
            fetchIntegrationsSummary()
        ]));
        navigate(routes.availableApps);
    };
    const chooseAccounts = async ()=>{
        logBI(biCodes.chooseAccounts, actorIds);
        reportQboChooseAccounts(accounting, reporters);
        await showModal({
            biName: 'map_accounts_modal',
            closeOnClickOutside: false,
            closeOnEscapeKey: false,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(MapAccountsModal, {
                    dismissModal: dismissModal,
                    accounting: accounting
                });
            }
        });
    };
    const mapContacts = async ()=>{
        logBI(biCodes.mapContacts, actorIds);
        reportQboMapContacts(accounting, reporters, actorIds);
        await showModal({
            biName: 'map_contacts_modal',
            closeOnClickOutside: false,
            closeOnEscapeKey: false,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(MapContactsModal, {
                    dismissModal: dismissModal,
                    accounting: accounting
                });
            }
        });
    };
    const mapServices = async ()=>{
        logBI(biCodes.mapServices, actorIds);
        reportQboMapServices(accounting, reporters, actorIds);
        await showModal({
            biName: 'map_services_modal',
            closeOnClickOutside: false,
            closeOnEscapeKey: false,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(MapServicesModal, {
                    dismissModal: dismissModal,
                    accounting: accounting
                });
            }
        });
    };
    return {
        disconnect,
        chooseAccounts,
        mapContacts,
        mapServices,
        isDisconnecting
    };
};
